import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AnimatePresence, motion } from 'framer-motion';
import api from '../../../api';
import { ReactComponent as ChatHistoryIcon } from '../../../icons/chat-history.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow-left.svg';
import './recents-drawer.scss';

interface HistoryThread {
  threadId: string;
  description: string | null;
  lastUpdatedAt: string | null;
  createdAt: string | null;
}

interface QuickAskHistoryResponse {
  success: boolean;
  threads: HistoryThread[];
  hasMore: boolean;
  nextOffset: number | null;
}

interface Props {
  open: boolean;
  agentId: string | undefined;
  onClose: () => void;
  /** Element that toggles the drawer; click-outside detection ignores it
   *  so toggling doesn't immediately close-then-reopen. */
  toggleRef?: React.RefObject<HTMLElement | null>;
}

const PAGE_SIZE = 20;

// ── Time helpers ──────────────────────────────────────────────────────────

/**
 * Compact relative-time formatting used in the right column of each item.
 * Matches the style in the design — "2m", "45m", "1d", "2w", etc.
 */
const formatRelative = (iso: string | null): string => {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  const seconds = Math.max(1, Math.floor(diffMs / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(days / 365);
  return `${years}y`;
};

// ── Component ─────────────────────────────────────────────────────────────

/**
 * Sliding drawer that appears next to the ExpansiveSideNavigation and
 * lists the user's recent QuickChat threads. Designed to be visually
 * docked against the right edge of the side nav (60px wide).
 *
 * Data source is identical to `QuickChatHistoryDropdown` — the
 * `/agents/quick-ask-history` endpoint — so the listing mirrors what
 * the inline history pill on QuickChatPage already shows.
 */
const RecentsDrawer: React.FC<Props> = ({
  open,
  agentId,
  onClose,
  toggleRef,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [threads, setThreads] = useState<HistoryThread[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const drawerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(
    async (reset: boolean) => {
      if (!agentId) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessTokenSilently();
        const nextOffset = reset ? 0 : offset;
        const res = await api.get<QuickAskHistoryResponse>(
          `/agents/quick-ask-history?agent_id=${encodeURIComponent(
            agentId,
          )}&limit=${PAGE_SIZE}&offset=${nextOffset}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const fetched = res.data.threads || [];
        setThreads((prev) => (reset ? fetched : [...prev, ...fetched]));
        setOffset(nextOffset + fetched.length);
        setHasMore(!!res.data.hasMore);
        setHasFetched(true);
      } catch (err) {
        console.error('Failed to load recent chats:', err);
        setError('Could not load recent chats.');
      } finally {
        setLoading(false);
      }
    },
    [agentId, getAccessTokenSilently, offset],
  );

  // Lazy-load the first page the first time the drawer opens.
  useEffect(() => {
    if (open && !hasFetched && !loading) {
      fetchPage(true);
    }
  }, [open, hasFetched, loading, fetchPage]);

  // Close on click outside (ignoring the toggle button).
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (toggleRef?.current && toggleRef.current.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, onClose, toggleRef]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading || !hasMore) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 80) {
      fetchPage(false);
    }
  }, [loading, hasMore, fetchPage]);

  const handleSelect = (threadId: string) => {
    // Hard navigate so QuickChatPage's hydration effect remounts and
    // boots from the selected thread (same pattern as the in-page
    // history dropdown).
    window.location.assign(`/?conversation_id=${encodeURIComponent(threadId)}`);
  };

  // Portaled to document.body so it lives outside the side-nav's
  // stacking context. The drawer is fixed at left: 60px (matching the
  // nav width) and uses a subtle offset+fade animation.
  return createPortal(
    <AnimatePresence>
      {open && (
        <React.Fragment key='recents-drawer-fragment'>
          {/* Shroud that dims the rest of the page (matches the
              AddTodoModal overlay treatment). Sits just to the right of
              the 60px-wide side nav so the nav strip stays visible. */}
          <motion.div
            key='recents-drawer-shroud'
            className='recents-drawer-shroud'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onClick={onClose}
          />
          <motion.aside
            ref={drawerRef}
            key='recents-drawer'
            className='recents-drawer'
            initial={{ x: -16, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -16, opacity: 0 }}
            transition={{
              type: 'spring',
              stiffness: 420,
              damping: 34,
              mass: 0.6,
            }}
          >
            <div className='recents-drawer-header'>
              <button
                type='button'
                className='drawer-mobile-close'
                onClick={onClose}
                aria-label='Close'
              >
                <ArrowLeftIcon />
              </button>
              <ChatHistoryIcon className='recents-drawer-header-icon' />
              <h2 className='recents-drawer-title'>Chats</h2>
              <p className='recents-drawer-subtitle'>
                Pick up where you left off across any device
              </p>
            </div>

            <div
              ref={scrollRef}
              className='recents-drawer-scroll'
              onScroll={handleScroll}
            >
              {error && <div className='recents-drawer-error'>{error}</div>}
              {!error && threads.length === 0 && !loading && hasFetched && (
                <div className='recents-drawer-empty'>
                  No previous chats yet.
                </div>
              )}
              {threads.map((t) => (
                <button
                  key={t.threadId}
                  type='button'
                  className='recents-drawer-item'
                  onClick={() => handleSelect(t.threadId)}
                >
                  <div className='recents-drawer-item-left'>
                    <div className='recents-drawer-item-title'>
                      {t.description || 'Untitled chat'}
                    </div>
                  </div>
                  <div className='recents-drawer-item-time'>
                    {formatRelative(t.lastUpdatedAt || t.createdAt)}
                  </div>
                </button>
              ))}
              {loading && (
                <div className='recents-drawer-loading'>Loading…</div>
              )}
            </div>
          </motion.aside>
        </React.Fragment>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default RecentsDrawer;
